import { Injectable } from '@angular/core';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { PaymentStatus } from '../models/payment-status.enum';
import { PaymentStore } from './payment.store';
import { ReservationStore } from './reservation-store';
import { Observable } from 'rxjs/Observable';
import { RsvpStore } from './rsvp.store';
import { EventStore } from './event.store';
import { AttachmentStore } from './attachment/attachment.store';
import { of } from 'rxjs/observable/of';
import { AgencyLookupStore } from 'app/shared/lookup/agency-lookup/agency-lookup.store';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { map } from 'rxjs/operators';
import { Payment } from '../models/payment';
import { List } from 'immutable';
import { Reservation } from '../models/reservation';

@Injectable()
export class EventValidationService {

  private static readonly NON_EDITABLE_STATUS_LIST = [
    PaymentStatus.READY,
    PaymentStatus.PENDING,
    PaymentStatus.INVOICED,
    PaymentStatus.INVOICE_REJECTED,
    PaymentStatus.FUNDED,
    PaymentStatus.PAY_SENT,
    PaymentStatus.PAY_REJECTED,
    PaymentStatus.ERROR,
    PaymentStatus.CANCELLED,
    PaymentStatus.CHECK_STOPPED,
    PaymentStatus.PAY_CLEARED,
    PaymentStatus.PAY_REISSUED,
    PaymentStatus.PENDING_WF_APPROVAL
  ];

  private static readonly EDITABLE_STATUS_LIST_FOR_AGENCY = [
    PaymentStatus.REQUEST_REJECTED,
    PaymentStatus.INVOICE_REJECTED
  ];

  private static readonly EDITABLE_CURRENCY_STATUS_LIST_FOR_AGENCY = [
    PaymentStatus.REQUEST_REJECTED,
    PaymentStatus.INVOICE_REJECTED,
    PaymentStatus.PAY_REJECTED
  ];

  private static readonly CONTACT_EDITABLE_STATUS_LIST_FOR_AGENCY = [
    PaymentStatus.REQUESTED,
    PaymentStatus.RESENT_REQUEST,
    PaymentStatus.REQUEST_REJECTED,
    PaymentStatus.PENDING,
    PaymentStatus.READY,
    PaymentStatus.FUNDED,
    PaymentStatus.INVOICED,
    PaymentStatus.INVOICE_REJECTED
  ];

  private static readonly PAID_STATUS_LIST = [
    PaymentStatus.PAID_DIRECT,
    PaymentStatus.PAY_SENT,
    PaymentStatus.PAY_CLEARED,
    PaymentStatus.CHECK_STOPPED,
    PaymentStatus.PAY_REISSUED
  ];

  constructor(private paymentStore: PaymentStore,
    private reservationStore: ReservationStore,
    private authenticationStore: AuthenticationStore,
    private rsvpStore: RsvpStore,
    private attachmentStore: AttachmentStore,
    private eventStore: EventStore,
    private agencyLookupStore: AgencyLookupStore) {
  }

  canEditContractSummary(): Observable<boolean | Observable<boolean>> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }
      return this.paymentStore.getLatestVendorFinalPayments().map(payments => {
        if (this.authenticationStore.hasPermission('MultiVendor')) {

          if (!selectedEvent.eventVendors || selectedEvent.eventVendors.length === 0) {
            return true;
          }

          return selectedEvent.eventVendors.some(vendor => {
            const payment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
            return !payment || !EventValidationService.PAID_STATUS_LIST.some(status => status === payment.status);
          });
        }
        return this.canChangeEvent();
      });
    });
  }

  canChangeReservations(): Observable<boolean> {
    return this.canChangeEvent();
  }

  canChangeRsvps(): Observable<boolean> {
    return this.canChangeEvent();
  }

  canAddAdjustments(): Observable<boolean> {
    return this.canChangeEvent();
  }

  canEditGroupCode(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }
      return this.paymentStore.getLatestFinalPayment().flatMap(latestFinalPayment => {
        return this.reservationStore.getEventHasReservations().flatMap(eventHasReservations => {
          return this.hasPrePayment().map(hasPrepayment => {
            if (this.authenticationStore.isPropertyUser()) {
              if (latestFinalPayment && PaymentStatus.REJECTED_WF_APPROVAL !== latestFinalPayment.status) {
                return false;
              }
              if (eventHasReservations) {
                return false;
              }
              if (hasPrepayment) {
                return false;
              }
            }
            return true;
          });
        });
      });
    });
  }

  canEditEventDates(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }
      return this.paymentStore.getLatestFinalPayment().flatMap(latestFinalPayment => {
        return this.reservationStore.getEventHasReservations().flatMap(eventHasReservations => {
          return this.rsvpStore.getEventHasRsvps().flatMap(eventHasRsvps => {
            return this.hasPrePayment().flatMap(hasPrepayment => {
              const agencyCode = selectedEvent && selectedEvent.eventAgency ? selectedEvent.eventAgency.code : undefined;
              if (!agencyCode) {
                return of(true);
              }
              return this.agencyLookupStore.isSubscriberAgency(selectedEvent.eventAgency.code).take(1).map(isSubscriberAgency => {
                if (this.authenticationStore.isPropertyUser()) {
                  if (!isSubscriberAgency) {
                    return true;
                  }
                }

                if (this.authenticationStore.hasPermission('MultiVendor')) {
                  if (selectedEvent.eventVendors && selectedEvent.eventVendors.some(vendor =>
                    PartyType.PROPERTY === vendor.partyType && 'Y' === vendor.createdEvent) &&
                    selectedEvent.eventAgency && selectedEvent.eventAgency.createdEvent !== 'Y') {
                      return false;
                    }
                }

                if (eventHasReservations) {
                  return false;
                }

                if (eventHasRsvps) {
                  const hotelCreatedEvent = selectedEvent.eventProperty && 'Y' === selectedEvent.eventProperty.createdEvent;
                  if (!this.authenticationStore.isPropertyUser() && hotelCreatedEvent) {
                    return false;
                  }
                }

                if (latestFinalPayment && latestFinalPayment.status) {
                  if (this.authenticationStore.isPropertyUser()) {
                    return PaymentStatus.REJECTED_WF_APPROVAL === latestFinalPayment.status;
                  } else {
                    if (!EventValidationService.EDITABLE_STATUS_LIST_FOR_AGENCY.some(status => status === latestFinalPayment.status)) {
                      return false;
                    }
                  }
                }

                if (hasPrepayment && this.authenticationStore.isPropertyUser() && isSubscriberAgency) {
                  return false;
                }

                return true;
              });
            });
          });
        });
      });
    });
  }

  canEditEventCurrency(): Observable<boolean> {
    return Observable.combineLatest(
      this.eventStore.selectedEvent,
      this.paymentStore.getLatestVendorFinalPayments(),
      this.reservationStore.getEventHasReservations(),
      this.rsvpStore.getEventHasRsvps(),
      this.hasVendorPrePayments()
    ).pipe(
        map(([selectedEvent, latestVendorFinalPayment, eventHasReservations, eventHasRsvps, hasVendorPrepayment]) => {
          if (!selectedEvent || !selectedEvent.eventId) {
            return true;
          }
          if (this.authenticationStore.hasPermission('MultiVendor')) {
            if (selectedEvent.eventVendors && selectedEvent.eventVendors.some(vendor =>
              PartyType.PROPERTY === vendor.partyType && 'Y' === vendor.createdEvent)) {
                return false;
              }
          }
          if (eventHasReservations === true) {
            return false;
          }
          if (eventHasRsvps) {
            return false;
          }
          if (hasVendorPrepayment) {
            return false;
          }
          if (this.authenticationStore.isPropertyUser() && latestVendorFinalPayment
              && latestVendorFinalPayment.length && latestVendorFinalPayment.length > 0
              && PaymentStatus.REJECTED_WF_APPROVAL !== latestVendorFinalPayment[0].status) {
            return false;
          }
          if (!this.authenticationStore.isPropertyUser() && latestVendorFinalPayment
              && latestVendorFinalPayment.length && latestVendorFinalPayment.length > 0) {
            if (this.somePaymentIsNotRejected(latestVendorFinalPayment)) {
              return false;
            }
          }
          return true;
        })
    );
  }

  private somePaymentIsNotRejected(latestVendorFinalPayment: Payment[]) {
    return latestVendorFinalPayment.some(payment => {
      const isRejectedPayment = EventValidationService.EDITABLE_CURRENCY_STATUS_LIST_FOR_AGENCY.some((status) => {
        // Comparing with == because status is a PaymentStatus enum and payment.status is a string at runtime
        // (even though it is declared as PaymentStatus in the Payment model)
        // tslint:disable-next-line:triple-equals
        return status == payment.status;
      });
      return !isRejectedPayment;
    });
  }

  canEditShoulderDates(): Observable<boolean> {
    return this.canChangeEvent();
  }

  private canChangeEvent(): Observable<boolean> {
    return this.paymentStore.getLatestFinalPayment().map((latestFinalPayment) => {
      if (this.authenticationStore.isPropertyUser() && latestFinalPayment && latestFinalPayment.status) {
        if (EventValidationService.NON_EDITABLE_STATUS_LIST.some(status => status === latestFinalPayment.status)) {
          return false;
        }
      }
      return true;
    });
  }

  private hasPrePayment(): Observable<boolean> {
    return this.paymentStore.getPrepayment().map((prepayment) => {
      if (prepayment) {
        return true;
      }
      return false;
    });
  }

  private hasVendorPrePayments(): Observable<boolean> {
    return this.paymentStore.getLatestVendorPrepayments().map((prepayments: Payment[]) => {
      if (prepayments && prepayments.length > 0) {
        return true;
      }
      return false;
    });
  }

  canChangeProperty(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }
      if (selectedEvent.eventProperty && selectedEvent.eventProperty.mergedEvent === 'Y') {
        return of(false);
      }
      return this.reservationStore.getEventHasReservations().flatMap(eventHasReservations => {
        return this.rsvpStore.getEventHasRsvps().flatMap(eventHasRsvps => {
          return this.paymentStore.getLatestFinalPayment().flatMap(latestFinalPayment => {
            return this.paymentStore.getLatestVendorPrepayments().flatMap(prepayments => {
              return this.attachmentStore.attachments.map(attachments => {
                if (eventHasReservations) {
                  return false;
                }
                if (eventHasRsvps) {
                  return false;
                }
                if (latestFinalPayment && latestFinalPayment.status) {
                  return false;
                }
                if (prepayments && prepayments.length > 0) {
                  return false;
                }
                if (attachments && attachments.size > 0) {
                  return false;
                }
                return true;
              });
            });
          });
        });
      });
    });
  }

  canChangeAgency(): Observable<boolean> {
    const finalPayments$: Observable<Payment[] | Payment> = this.authenticationStore.isMultiVendor()
      ? this.paymentStore.getLatestVendorFinalPayments()
      : this.paymentStore.getLatestFinalPayment();
    const hasPrepayment$: Observable<boolean> = this.authenticationStore.isMultiVendor()
      ? this.hasVendorPrePayments()
      : this.hasPrePayment();

    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }
      return Observable.combineLatest<boolean, Payment | Payment[], boolean>(
          this.reservationStore.getEventHasReservations(),
          finalPayments$,
          hasPrepayment$
        ).pipe(
          map(([eventHasReservations, latestFinalPayment, hasPrepayment]) => {
            if (this.authenticationStore.isPropertyUser()) {
              const hotelCreatedEvent = selectedEvent.eventProperty && 'Y' === selectedEvent.eventProperty.createdEvent;
              if (!hotelCreatedEvent) {
                return false;
              }
            }
            if (hasPrepayment) {
              return false;
            }
            if (eventHasReservations) {
              return false;
            }
            if (latestFinalPayment) {
              if (Array.isArray(latestFinalPayment)) {
                if (latestFinalPayment.length && latestFinalPayment.length > 0) {
                  if (latestFinalPayment.some((payment) => payment && payment.status)) {
                    return false;
                  }
                }
              } else if (latestFinalPayment.status) {
                return false;
              }
            }
            return true;
          })
        );
    });
  }

  canAddVendor(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }

      if (!selectedEvent.eventVendors || selectedEvent.eventVendors.length === 0) {
        return of(true);
      }

      return this.paymentStore.getLatestVendorFinalPayments().map(payments => {
        return selectedEvent.eventVendors.some(vendor => {
          const payment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
          return !payment || !EventValidationService.PAID_STATUS_LIST.some(status => status === payment.status);
        });
      });
    });
  }

  canRemoveVendor(vendor: Party): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }

      if (!vendor) {
        return of(true);
      }

      // subscriber vendor created the event
      if (vendor.subscriber && vendor.createdEvent === 'Y' && vendor.mergedEvent !== 'Y') {
        return of(false);
      }

      return this.paymentStore.getLatestVendorPrepayments().flatMap(prepayments => {
        return this.paymentStore.getLatestVendorFinalPayments().flatMap(payments => {
          return this.reservationStore.getHasReservationsByEventPartyId(vendor.eventPartyId).flatMap(hasReservationsByEventPartyId => {
            return this.rsvpStore.getHasRsvpsByEventPartyId(vendor.eventPartyId).map(hasRsvpsByEventPartyId =>  {
              // vendor has NOT prepayments
              const prepayment = prepayments && prepayments.length > 0 ?
              prepayments.filter(prepay => prepay.eventPartyId === vendor.eventPartyId)[0] : null;
              if (prepayment) {
                return false;
              }

              // vendor has NOT last final payment with status REQUEST_REJECTED and INVOICE_REJECTED
              const latestFinalPayment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
              if (latestFinalPayment && latestFinalPayment.status
                && !EventValidationService.EDITABLE_STATUS_LIST_FOR_AGENCY.some(status => status === latestFinalPayment.status)) {
                return false;
              }

              if (hasReservationsByEventPartyId) {
                return false;
              }

              if (hasRsvpsByEventPartyId) {
                return false;
              }

              return true;
            });
          });
        });
      });
    });
  }

  canEditVendorContact(vendor: Party): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }

      if (!vendor) {
        return of(true);
      }

      return this.paymentStore.getLatestVendorFinalPayments().map(payments => {
        // vendor has NOT last final payment with status REQUEST_REJECTED and INVOICE_REJECTED
        const latestFinalPayment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
        if (latestFinalPayment && latestFinalPayment.status
          && !EventValidationService.CONTACT_EDITABLE_STATUS_LIST_FOR_AGENCY.some(status => status === latestFinalPayment.status)) {
          return false;
        }

        return true;
      });
    });
  }

  canEditVendorCommRate(vendor: Party): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }

      if (!vendor) {
        return of(true);
      }
      return this.reservationStore.getHasReservationsByEventPartyId(vendor.eventPartyId).flatMap(hasReservationsByEventPartyId => {
        return this.paymentStore.getLatestVendorPrepayments().flatMap(prepayments => {
          return this.paymentStore.getLatestVendorFinalPayments().map(payments => {

            if (vendor.subscriber) {
              if (hasReservationsByEventPartyId) {
                return false;
              }

              // vendor has NOT prepayments
              const prepayment = prepayments && prepayments.length > 0 ?
                prepayments.filter(prepay => prepay.eventPartyId === vendor.eventPartyId)[0] : null;
              if (prepayment) {
                return false;
              }

              // vendor has NOT final payments
              const latestFinalPayment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
              if (latestFinalPayment && latestFinalPayment.status && PaymentStatus.REJECTED_WF_APPROVAL !== latestFinalPayment.status) {
                return false;
              }
            } else {
              // vendor has NOT last final payment with status REQUEST_REJECTED and INVOICE_REJECTED
              const latestFinalPayment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
              if (latestFinalPayment && latestFinalPayment.status
                && !EventValidationService.EDITABLE_STATUS_LIST_FOR_AGENCY.some(status => status === latestFinalPayment.status)) {
                return false;
              }
            }
            return true;
          });
        });
      });

    });
  }

  canEditAgencyContact(): Observable<boolean> {
    return this.canEditContact();
  }

  canEditClientContact(): Observable<boolean> {
    return this.canEditContact();
  }

  private canEditContact(): Observable<boolean> {
    return this.eventStore.selectedEvent.flatMap(selectedEvent => {
      if (!selectedEvent || !selectedEvent.eventId) {
        return of(true);
      }

      return this.paymentStore.getLatestVendorFinalPayments().map(payments => {
        if (!this.authenticationStore.isPropertyUser()) {
          return selectedEvent.eventVendors.some(vendor => {
            const payment = payments != null ? payments.filter(pay => pay.eventPartyId === vendor.eventPartyId)[0] : null;
            return !payment || EventValidationService.CONTACT_EDITABLE_STATUS_LIST_FOR_AGENCY.some(status => status === payment.status);
          });
        }
        return true;
      });
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/event-validation.service.ts